import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {getFiltersFromUrl, FilterForStore} from 'utils/filters';

import {composeActions} from 'ducks/common';
import {storeFiltersInUrl} from 'ducks/urls';
import {
  FILTERS,
  selectFilters,
  setFilter,
  setFilters,
  overwriteFilterObject
} from 'ducks/filters';

export interface WithFiltersProps<F extends FilterForStore> {
  setFilter: (field: string, value: any) => any;
  getFilters: () => F;
  clearFilters: () => any;
}

const defaultOptions = {
  saveToUrl: false,
  clearFiltersOnUnmount: true
};

const withFilters = <T extends any, F extends FilterForStore>(
  Component: React.ComponentType<T>,
  namespace: string,
  options: {initialFilters: F} & Partial<typeof defaultOptions>
) => {
  const combinedOptions = {...defaultOptions, ...options};

  class WrappedComponent extends React.Component<any> {
    componentDidMount() {
      let urlFilters = {};

      if (combinedOptions.saveToUrl) {
        urlFilters = getFiltersFromUrl(combinedOptions.initialFilters);
      }

      if (combinedOptions.saveToUrl && !combinedOptions.clearFiltersOnUnmount) {
        this.props.storeFiltersInUrl({
          filtersSelector: state => selectFilters(state, namespace)
        });
      }

      this.props.setFilters({
        filters: {...this.getFilters(), ...urlFilters},
        namespace
      });
    }

    componentWillUnmount() {
      if (combinedOptions.saveToUrl) {
        this.props.storeFiltersInUrl({filtersSelector: () => ({})});
      }
      if (combinedOptions.clearFiltersOnUnmount) {
        this.props.overwriteFilterObject({filters: {}, namespace});
      }
    }

    setFilter = (field, value) => {
      return this.props.composeActions([
        () => setFilter({field, value, namespace}),
        ...this.storeFiltersToUrlExtraActions()
      ]);
    };

    clearFilters = () => {
      return this.props.composeActions([
        () =>
          setFilters({
            filters: combinedOptions.initialFilters,
            namespace
          }),
        ...this.storeFiltersToUrlExtraActions()
      ]);
    };

    storeFiltersToUrlExtraActions = () => {
      if (combinedOptions.saveToUrl) {
        return [
          () =>
            storeFiltersInUrl({
              filtersSelector: state => selectFilters(state, namespace)
            })
        ];
      }
      return [];
    };

    getFilters = () => {
      if (_.isEmpty(this.props.stateFilters)) {
        return combinedOptions.initialFilters;
      }
      return this.props.stateFilters;
    };

    render() {
      // This downcast is important to remove the added props of the WrappedComponent
      const restOfProps: T = this.props;
      const getFilters: () => F = this.getFilters;

      return (
        <Component
          {...restOfProps}
          setFilter={this.setFilter}
          getFilters={getFilters}
          clearFilters={this.clearFilters}
        />
      );
    }
  }

  const mapStateToProps = state => {
    return {
      stateFilters: selectFilters(state, namespace)
    };
  };

  const mapDispatchToProps = {
    setFilter,
    setFilters,
    composeActions,
    storeFiltersInUrl,
    overwriteFilterObject
  };

  // TODO: at some point create a HOC type following the `connect` type pattern
  return connect(mapStateToProps, mapDispatchToProps)(
    WrappedComponent as React.ComponentType<T>
  );
};

export {withFilters, FILTERS};
